import { ArrowLeft } from "@phosphor-icons/react";
import MainLayout from "../components/MainLayout";
import Separator from "../components/Separator";
import IconButton from "../components/IconButton";
import Typography from "../components/Typography";
import {
    CalculationVeracityDiagram,
    CalculationVeracityNode,
} from "../components/CalculationVeracityDiagram";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Edge, Position } from "@xyflow/react";
import { formatNumber } from "../utils/formatNumber";
import { appPaths } from "../Routes";
import {
    AssetFinancedEmissionsOriginDto,
    useSectorFinancedEmissionsOrigin,
} from "../api/reports";
import { useCallback, useMemo } from "react";

const calcPosition = (
    index: number,
    defaultX: number,
    defaultY: number,
    offsetX: number,
    offsetY: number
) => {
    return { x: index * defaultX + offsetX, y: index * defaultY + offsetY };
};

const xGap = 60;
const yGap = 30;

const nodeHeight = 60;
const nodeWidth = 300;

const CalculationVeracityPage = () => {
    const params = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const createNodes = useCallback(
        (
            origin: AssetFinancedEmissionsOriginDto,
            index: number,
            arr: AssetFinancedEmissionsOriginDto[]
        ): CalculationVeracityNode[] => {
            const length = arr.length;

            return [
                {
                    id: index + "fe",
                    sourcePosition: Position.Right,
                    targetPosition: Position.Left,
                    type: "linkNode",
                    data: {
                        label: `${origin.assetName} Financed Emissions`,
                        value: `${formatNumber(
                            origin?.financedEmissions
                        )}kg CO2e`,
                        onClick: () =>
                            navigate(
                                appPaths.emissionsSourceDetail(
                                    origin.assetId.toString()
                                )
                            ),
                        className: "bg-teal-60 text-surface-light-5 border-surface-light-5"
                    },
                    position: calcPosition(
                        index,
                        0,
                        4 * nodeHeight + yGap,
                        xGap + nodeWidth,
                        (-length * 4 * nodeHeight + yGap) / 2 +
                            (nodeHeight + yGap) / 2
                    ),
                    style: {
                        width: nodeWidth,
                        height: nodeHeight,
                    },
                },
                {
                    id: index + "re",
                    targetPosition: Position.Left,
                    type: "linkNode",
                    data: {
                        label: `${origin.assetName} Reported Emissions`,
                        value: `${formatNumber(origin.totalC02e)}kg CO2e`,
                        onClick: () =>
                            navigate(
                                appPaths.activityData +
                                    `?dataSourceIdAd=${origin.dataSourceId}&dateRange=all`
                            ),
                        className: "bg-surface-light-5 text-surface-dark-5 border-surface-dark-5"
                    },
                    position: calcPosition(
                        index,
                        0,
                        4 * nodeHeight + yGap,
                        2 * (xGap + nodeWidth),
                        (-length * 4 * nodeHeight + yGap) / 2 +
                            (nodeHeight + yGap) / 2 -
                            1 * nodeHeight
                    ),
                    style: {
                        width: nodeWidth,
                        height: nodeHeight,
                    },
                },
                {
                    id: index + "af",
                    sourcePosition: Position.Right,
                    targetPosition: Position.Left,
                    type: "linkNode",
                    data: {
                        label: "Attribution Factor",
                        value: formatNumber(
                            origin.outstandingAmount / origin.enterpriseValue
                        ),
                        className: "bg-surface-light-5 text-surface-dark-5 border-surface-dark-5"
                    },
                    style: {
                        width: nodeWidth,
                        height: nodeHeight,
                    },
                    position: calcPosition(
                        index,
                        0,
                        4 * nodeHeight + yGap,
                        2 * (xGap + nodeWidth),
                        (-length * 4 * nodeHeight + yGap) / 2 +
                            (nodeHeight + yGap) / 2 +
                            1 * nodeHeight
                    ),
                },
                {
                    id: index + "oa",
                    targetPosition: Position.Top,
                    type: "linkNode",
                    data: {
                        label: "Outstanding Amount",
                        value: `£${formatNumber(origin.outstandingAmount)}`,
                        onClick: () =>
                            navigate(
                                appPaths.evidence +
                                    `?searchRee=${origin.assetName}&statusFilter=All`
                            ),
                        className: "bg-white text-surface-dark-5 border-surface-dark-5"
                    },
                    style: {
                        width: nodeWidth,
                        height: nodeHeight,
                    },
                    position: calcPosition(
                        index,
                        0,
                        4 * nodeHeight + yGap,
                        3 * (xGap + nodeWidth),
                        (-length * 4 * nodeHeight + yGap) / 2 +
                            (nodeHeight + yGap) / 2 +
                            2 * nodeHeight
                    ),
                },
                {
                    id: index + "cv",
                    targetPosition: Position.Bottom,
                    type: "linkNode",
                    data: {
                        label: "Company Value",
                        value: `£${formatNumber(origin.enterpriseValue)}`,
                        onClick: () =>
                            navigate(
                                appPaths.evidence +
                                    `?searchRee=${origin.assetName}&statusFilter=All`
                            ),
                        className: "bg-white text-surface-dark-5 border-surface-dark-5"
                    },
                    style: {
                        width: nodeWidth,
                        height: nodeHeight,
                    },
                    position: calcPosition(
                        index,
                        0,
                        4 * nodeHeight + yGap,
                        3 * (xGap + nodeWidth),
                        (-length * 4 * nodeHeight + yGap) / 2 +
                            (nodeHeight + yGap) / 2
                    ),
                },
            ];
        },
        [navigate]
    );

    const createEdges = (_: AssetFinancedEmissionsOriginDto, index: number) => {
        return [
            {
                id: "root-" + index + "fe",
                source: "root",
                type: "smoothstep",
                target: index + "fe",
            },
            {
                id: "fe-" + index + "re",
                source: index + "fe",
                type: "smoothstep",
                target: index + "re",
            },
            {
                id: "fe-" + index + "af",
                source: index + "fe",
                type: "smoothstep",
                target: index + "af",
            },
            {
                id: "af-" + index + "oa",
                source: index + "af",
                type: "smoothstep",
                target: index + "oa",
            },
            {
                id: "af-" + index + "cv",
                source: index + "af",
                type: "smoothstep",
                target: index + "cv",
            },
        ];
    };

    const { sectorFinancedEmissionsOrigin, isLoading, scopes, year } =
        useSectorFinancedEmissionsOrigin(
            params.sector as string,
            searchParams.get("year") ?? "2025",
            searchParams.get("scopes") ?? "1,2,3"
        );

    const scopeDescription = `Scope ${scopes.join(
        "/"
    )} Financed Emissions ${year}`;

    const initialNodes: CalculationVeracityNode[] = useMemo(
        () => [
            {
                id: "root",
                sourcePosition: Position.Right,
                type: "linkNode",
                data: {
                    label: `Total Financed Emissions: ${formatNumber(
                        sectorFinancedEmissionsOrigin?.totalFinancedEmissions
                    )}tons CO2e`,
                    className: "bg-deep-ocean-20 text-surface-light border-surface-light-5"
                },
                style: {
                    height: nodeHeight,
                    width: nodeWidth,
                },
                position: { x: 0, y: 0 },
            },
            ...(sectorFinancedEmissionsOrigin?.assetOrigins.flatMap(
                createNodes
            ) ?? []),
        ],
        [
            createNodes,
            sectorFinancedEmissionsOrigin?.assetOrigins,
            sectorFinancedEmissionsOrigin?.totalFinancedEmissions,
        ]
    );

    const initialEdges: Edge[] = useMemo(
        () =>
            sectorFinancedEmissionsOrigin?.assetOrigins.flatMap(createEdges) ??
            [],
        [sectorFinancedEmissionsOrigin?.assetOrigins]
    );

    if (!sectorFinancedEmissionsOrigin) {
        return <></>;
    }

    return (
        <MainLayout isLoading={isLoading}>
            <div className="pt-14 pb-11">
                <div className="flex flex-col sm:flex-row gap-6 items-center pb-5 px-6">
                    <div className="flex items-center">
                        <IconButton
                            label="Back"
                            icon={ArrowLeft}
                            iconSize="large"
                            className="mr-3"
                            onClick={() => window.history.back()}
                        />
                        <Typography variant="headline-small" component="h1">
                            {`${sectorFinancedEmissionsOrigin?.sectorName} ${scopeDescription} Origin`}
                        </Typography>
                    </div>
                </div>
                <Separator className="mb-6" />
                <CalculationVeracityDiagram
                    nodes={initialNodes}
                    edges={initialEdges}
                />
            </div>
        </MainLayout>
    );
};

export default CalculationVeracityPage;
